import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Box, Typography } from '@mui/material';

import { FHButton, FHConfirmDialog } from '../../../../../components/base';
import { FCheckbox, FQrCode, FSelect } from '../../../../../components/baseV2';
import { FORTA_ERROR } from '../../../../../utils/colors';
import { EVV_FALLBACK_REASON_OPTIONS } from './constants';
import { useEvvDesktopFallback, useEvvQrCountdown } from './hooks';
import {
  EVV_QR_CODE_SX,
  EVV_QR_COUNTDOWN_SX,
  EVV_QR_DIALOG_SX,
  EVV_QR_FALLBACK_BOX_SX,
  EVV_QR_FALLBACK_ROW_SX,
  EVV_QR_FOOTER_SX,
  EVV_QR_LOCATION_ERROR_SX,
  EVV_QR_REASON_SELECT_SX,
  EVV_QR_TITLE_SX,
  EVV_QR_TITLE_TEXT_SX,
} from './styles';

import type { ChangeEvent } from 'react';
import type { EvvQrDialogProps } from './types';

function EvvQrDialog({ open, kind, route, sessionId, onClose }: EvvQrDialogProps) {
  const { remainingSeconds, expired } = useEvvQrCountdown(open);
  const {
    fallbackChecked,
    reason,
    submitting,
    locationError,
    toggleFallback,
    selectReason,
    submitFallback,
  } = useEvvDesktopFallback({ open, kind, sessionId });

  const handleFallbackChange = (event: ChangeEvent<HTMLInputElement>) =>
    toggleFallback(event.target.checked);

  return (
    <FHConfirmDialog
      open={open}
      hideCloseIcon
      hideSubmitButton
      hideCancelButton
      dialogStyles={EVV_QR_DIALOG_SX}
      footerContainerStyles={{ display: 'none' }}
      titleRenderer={() => (
        <Box sx={EVV_QR_TITLE_SX}>
          <WarningAmberOutlinedIcon sx={{ color: FORTA_ERROR, fontSize: 28 }} />
          <Typography component="h2" sx={EVV_QR_TITLE_TEXT_SX}>
            Scan the QR with your mobile phone to continue verification
          </Typography>
        </Box>
      )}
      contentRenderer={() => (
        <>
          <Box sx={EVV_QR_CODE_SX}>
            <FQrCode route={route} size={262} />
          </Box>
          {expired && (
            <Box sx={EVV_QR_FALLBACK_BOX_SX}>
              <FCheckbox
                label="Unable to scan QR code with my mobile phone"
                checked={fallbackChecked}
                disabled={submitting}
                onChange={handleFallbackChange}
              />
              {fallbackChecked && (
                <Box sx={EVV_QR_FALLBACK_ROW_SX}>
                  <Box sx={EVV_QR_REASON_SELECT_SX}>
                    <FSelect
                      data-testid="evvFallbackReasonSelect"
                      label="Reason"
                      required
                      fullWidth
                      placeholder="Select reason"
                      disabled={submitting}
                      options={EVV_FALLBACK_REASON_OPTIONS}
                      optionDataTestIdPrefix="evvFallbackReasonItem"
                      value={reason ?? ''}
                      onUpdate={selectReason}
                    />
                  </Box>
                  <FHButton
                    data-testid="evvFallbackContinueButton"
                    loading={submitting}
                    disabled={!reason}
                    onClick={submitFallback}
                  >
                    Continue
                  </FHButton>
                </Box>
              )}
              {locationError && (
                <Typography sx={EVV_QR_LOCATION_ERROR_SX}>
                  Allow location access in your browser to continue.
                </Typography>
              )}
            </Box>
          )}
          <Box sx={EVV_QR_FOOTER_SX}>
            <FHButton
              data-testid="evvQrCancelButton"
              secondary
              disabled={submitting}
              onClick={onClose}
            >
              Cancel
            </FHButton>
            <Typography sx={EVV_QR_COUNTDOWN_SX}>
              {String(remainingSeconds).padStart(2, '0')} sec
            </Typography>
          </Box>
        </>
      )}
    />
  );
}

export default EvvQrDialog;
